import { useChat } from '@ai-sdk/react';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { toAISdkV5Messages } from '@mastra/ai-sdk/ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import AIAssistantHeader from '../../components/AIAssistantHeader';
import { chatTransport } from '../../lib/transporters';
import { useAIAssistant } from '../../store';
import { AGENTS, type ResourceId } from '../../types';
import AIAssistantContent from './AIAssistantContent';

type AIAssistantChatProps = {
  showHeader?: boolean;
  handleGoBack?: () => void;
  isExpanded?: boolean;
  onExpand?: () => void;
  children: React.ReactNode | ((props: { sendMessage: (message?: { text: string } | { parts: any[] } | { files: FileList | any[] }) => Promise<void> }) => React.ReactNode);
  placeholder?: string;
  resetChat?: boolean;
  headerActions?: React.ReactNode;
};

const AIAssistantChat = ({ showHeader, handleGoBack, children, placeholder, isExpanded, onExpand, headerActions }: AIAssistantChatProps) => {
  const { threadId, thread, refetchThread, refetchThreads, newThreadId, setThreadId, resetNewThreadId } = useAIAssistant();
  const { getUser, getSession } = useUser();
  const user = getUser();
  const session = getSession();
  const titleRefetchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const initialMessages = useMemo(() => {
    return thread?.messages ? toAISdkV5Messages(thread?.messages) : [];
  }, [thread?.messages]);

  const transport = useMemo(() => {
    const userId = user?.id ?? 'unknownUser';
    const fleetId = session?.fleet?.id ?? 'unknownFleet';
    const resourceId: ResourceId = `assistant_${userId}_${fleetId}`;
    const url = window.location.pathname;

    return chatTransport({
      agentName: AGENTS.agentFleetManager,
      source: 'web',
      component: 'sidepanel',
      threadId: threadId || newThreadId,
      resourceId,
      url,
    });
  }, [threadId, newThreadId, user?.id, session?.fleet?.id]);

  useEffect(() => {
    return () => clearTimeout(titleRefetchTimerRef.current);
  }, []);

  const { messages, error, status, sendMessage, regenerate, stop, setMessages } = useChat({
    id: threadId || newThreadId,
    transport,
    messages: initialMessages,
    onFinish: async ({ message }) => {
      if (message.role !== 'assistant') return;
      if (!threadId) setThreadId(newThreadId);
      await refetchThread();
      // Delayed refetch to pick up the backend-generated thread title
      clearTimeout(titleRefetchTimerRef.current);
      titleRefetchTimerRef.current = setTimeout(() => refetchThreads(), 2000);
    },
  });

  const handleNewConversation = useCallback(() => {
    clearTimeout(titleRefetchTimerRef.current);
    stop();
    setMessages([]);
    setThreadId('');
    resetNewThreadId();
  }, [stop, setMessages, setThreadId, resetNewThreadId]);

  useEffect(() => {
    if (threadId && initialMessages.length > 0 && messages.length === 0) {
      setMessages(initialMessages);
    }
  }, [threadId, initialMessages, messages.length, setMessages]);

  const resolvedMessages = messages.length > 0 ? messages : initialMessages;
  const hasMessages = resolvedMessages.length > 0;
  const emptyState = typeof children === 'function' ? children({ sendMessage }) : children;

  return (
    <section className='flex h-full flex-1 flex-col bg-ai-panel-gradient'>
      <AIAssistantHeader
        showHeader={showHeader}
        handleGoBack={handleGoBack}
        isExpanded={isExpanded}
        onExpand={onExpand}
        hasMessages={hasMessages}
        headerActions={headerActions}
        onNewConversation={handleNewConversation}
      />
      <AIAssistantContent
        emptyState={emptyState}
        placeholder={placeholder}
        messages={resolvedMessages}
        status={status}
        error={error}
        sendMessage={sendMessage}
        stop={stop}
        regenerate={regenerate}
        onRetry={handleNewConversation}
      />
    </section>
  );
};

export default AIAssistantChat;
